import { Link } from 'react-router-dom';
import { Circle, CircleCheck, Mic } from 'lucide-react';
import { useApi } from '../lib/useApi';

interface Progress {
  streakDays: number;
  activeDays: number;
  heritage: { stories: number; clips: number; people: number; events: number; generations: number };
  abilities: { code: string; statement: string; track: string; level: number; unlocked: boolean }[];
}

/**
 * What the family has kept, and what a child can now do.
 *
 * Two deliberate choices. The streak belongs to the household, not to a child —
 * a grandmother recording a proverb counts, and no one is made to feel they
 * broke anyone's run. And progress is written as ability ("greet an elder the
 * way your family would"), never as points, because points measure the app and
 * abilities measure the child.
 */
export function FamilyProgress() {
  const { data, error, loading, reload } = useApi<Progress>('/family/progress');

  // A panel that silently disappears when the server is unhappy is worse than
  // one that says so: the family thinks their recordings are gone, and nobody
  // watching the app can tell a failure from an empty family.
  if (loading) return null;
  if (error || !data) {
    return (
      <section className="card stack">
        <h2 style={{ margin: 0 }}>Your family's heritage</h2>
        <p className="muted">
          We couldn't load this just now — nothing has been lost. {error ?? 'The server did not answer.'}
        </p>
        <div>
          <button className="btn small secondary" onClick={() => void reload()}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  const { heritage, abilities, streakDays } = data;
  const unlocked = abilities.filter((a) => a.unlocked);
  const next = abilities.filter((a) => !a.unlocked).slice(0, 3);
  const nothingYet = heritage.stories === 0 && heritage.clips === 0 && heritage.events === 0;

  return (
    <section className="card stack">
      <div className="spread">
        <h2 style={{ margin: 0 }}>Your family's heritage</h2>
        <Link className="btn small ghost" to="/preserve">
          <Mic size={16} strokeWidth={1.75} aria-hidden="true" /> Preserve something
        </Link>
      </div>

      <div className="streak-band">
        <span>
          <strong>{heritage.stories}</strong> <span className="muted small">recording{heritage.stories === 1 ? '' : 's'} kept</span>
        </span>
        <span>
          <strong>{heritage.people}</strong> <span className="muted small">{heritage.people === 1 ? 'person' : 'people'} on the tree</span>
        </span>
        <span>
          <strong>{heritage.generations}</strong> <span className="muted small">generation{heritage.generations === 1 ? '' : 's'}</span>
        </span>
        <span>
          <strong>{heritage.events}</strong> <span className="muted small">moment{heritage.events === 1 ? '' : 's'} on the timeline</span>
        </span>
        {streakDays > 0 && (
          <span>
            <strong>{streakDays}</strong> <span className="muted small">day{streakDays === 1 ? '' : 's'} together in a row</span>
          </span>
        )}
      </div>

      {nothingYet && (
        <p className="muted">
          Nothing kept yet. The first recording is usually the hardest to ask for and the one everyone is gladdest to have — start with a proverb, it takes twenty
          seconds.
        </p>
      )}

      {abilities.length > 0 && (
        <>
          <h3 style={{ margin: '8px 0 0' }}>What your children can do</h3>
          <ul className="can-do">
            {unlocked.map((a) => (
              <li key={a.code} className="unlocked">
                <span className="mark" aria-hidden="true">
                  <CircleCheck size={17} strokeWidth={1.9} />
                </span>
                <span>{a.statement}</span>
              </li>
            ))}
            {next.map((a) => (
              <li key={a.code}>
                <span className="mark" aria-hidden="true">
                  <Circle size={17} strokeWidth={1.9} />
                </span>
                <span className="muted">{a.statement}</span>
              </li>
            ))}
          </ul>
          {unlocked.length === 0 && <p className="muted small">These fill in as lessons are finished — no points, no levels, just what they can actually say.</p>}
        </>
      )}
    </section>
  );
}
